import { createContext, useState, useEffect, useContext, Dispatch, SetStateAction, ReactNode } from 'react';
import { updateActiveTask, Util } from '../../util/Util';
import { Item } from '../../util/Item';
import { v4 as uuidv4 } from 'uuid';
import { BatchTask } from "../../util/Batch";
import { ConversionKind, isConversionKind, produceFromSource } from '../../util/conversionMath';
import { businessToday } from "../../util/businessClock";

import { lineTotal } from "../../util/quantity";
export interface ConversionTask<T> extends BatchTask<T> {
	depot: number;
	convType: ConversionKind | "";
	source_item: number;
	source_item_desc: string;
	dest_item: number;
	dest_item_desc: string;
	quantity: number;
	produce_qty: number;
	factor: number;
	source_cost: number;
	dest_cost: number;
}

interface ContextProps {
	pendingTasks: ConversionTask<Item>[];
	setPendingTasks: Dispatch<SetStateAction<ConversionTask<Item>[]>>;
	fetchedTask: any | null;
	setFetchedTask: (task: any) => void;
	addNewTask: () => void;
	activeTask: ConversionTask<Item> | undefined;
	items: any[];
	setItems: Dispatch<SetStateAction<any[]>>;
	sourceStock: number;
	destStock: number;
	finalQuantity: number;
}

const ConversionContext = createContext<ContextProps | undefined>(undefined);

interface ProviderProps {
	children: ReactNode;
}


export const ConversionProvider = ({ children }: ProviderProps) => {
	const [pendingTasks, setPendingTasks] = useState<ConversionTask<Item>[]>([]);
	const [fetchedTask, setFetchedTask] = useState<any>(null);
	const [items, setItems] = useState<any[]>([]);

	const activeTask = pendingTasks?.find((task) => task.active);
	const sourceStock = activeTask
		?
		(items.find((item: any) => Number(item.id) === activeTask.source_item)?.Stock ?? 0) + (fetchedTask && fetchedTask.source_item_id === activeTask.source_item ? fetchedTask.quantity : 0)
		: 0;

	const finalQuantity = activeTask
		? (Number(activeTask.produce_qty) > 0
			? Number(activeTask.produce_qty)
			: produceFromSource(activeTask.convType, activeTask.quantity || 0, activeTask.factor || 1))
		: 0;

	const destStock = activeTask
		?
		(items.find((item: any) => item.id === activeTask.dest_item)?.Stock ?? 0) - (fetchedTask && fetchedTask.dest_item_id === activeTask.dest_item ? fetchedTask.total_qty : 0)
		: 0;

	const addNewTask = () => {
		const newTask: ConversionTask<Item> = {
			id: uuidv4(),
			items: [],
			active: true,
			date: businessToday(),
			reference: Util.getConversionNumber(),
			receiver: -1,
			depot: -1,
			convType: "",
			description: "",
			source_item: -1,
			source_item_desc: "",
			dest_item: -1,
			dest_item_desc: "",
			quantity: 1,
			produce_qty: 1,
			factor: 1,
			source_cost: 0,
			dest_cost: 0,
			createdAt: new Date(),
			updatedAt: new Date(),
			total: function () {
				return lineTotal(finalQuantity, this.dest_cost);
			},
		};

		setPendingTasks((prevTasks) =>
			prevTasks.map((task) => ({ ...task, active: false })).concat(newTask)
		);
	};

	useEffect(() => {
		if (fetchedTask) {

			updateActiveTask(setPendingTasks, {
				...activeTask,
				id: fetchedTask.batch,
				date: fetchedTask.date,
				depot: fetchedTask.depot_id,
				receiver: fetchedTask.client_id,
				reference: fetchedTask.reference,
				description: fetchedTask.description,
				convType: isConversionKind(fetchedTask.type) ? fetchedTask.type : "",
				source_item: fetchedTask.source_item_id,
				source_item_desc: fetchedTask.source_item,
				dest_item: fetchedTask.dest_item_id,
				dest_item_desc: fetchedTask.dest_item,
				quantity: fetchedTask.quantity,
				produce_qty: produceFromSource(fetchedTask.type, fetchedTask.quantity, fetchedTask.factor),
				factor: fetchedTask.factor,
				source_cost: fetchedTask.source_amount,
				dest_cost: fetchedTask.amount
			});
		}
	}, [fetchedTask]);

	const value: ContextProps = {
		pendingTasks,
		setPendingTasks,
		fetchedTask,
		setFetchedTask,
		addNewTask,
		activeTask,
		items,
		setItems,
		sourceStock,
		destStock,
		finalQuantity,
	};

	return (
		<ConversionContext.Provider value={value}>
			{children}
		</ConversionContext.Provider>
	);
};

export const useConversion = () => {
	const context = useContext(ConversionContext);

	if (!context) {
		throw new Error('useConversion must be used within a ConversionProvider');
	}
	return context;
};